import {
  Anchor,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Image,
  List,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconCheck, IconDownload } from "@tabler/icons-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  imgDinLabelLaptop,
  imgEtiquetaConMarcaDeAgua,
  imgEtiquetaSinMarcaDeAgua,
} from "../shared/images";
import { getCurrentUser } from "../shared/getCurrentUser";
import { RestApi } from "../shared/RestApi";
import { Log } from "../shared/logging/Log";
import { NavBarContainer } from "./NavBarContainer";

const DOWNLOAD_URL =
  "https://labelmaster.azurewebsites.net/labelmaster/setup.exe";

const SESSION_EMAIL_KEY = "demoDownloadEmail";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const panelStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid var(--mantine-color-gray-2)",
  borderRadius: "var(--mantine-radius-md)",
  boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
  height: "100%",
};

function startDownload() {
  window.location.href = DOWNLOAD_URL;
}

export function InstallPage() {
  const { t } = useTranslation();
  const user = getCurrentUser();
  const isSignedIn = !!user;
  const alreadyBought = Boolean(user?.Bought);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDownloadClick = React.useCallback(
    (event: React.MouseEvent) => {
      Log.logger.logReportData("InstallPageCtaClicked", {
        signedIn: isSignedIn,
        alreadyBought,
      });

      if (isSignedIn) {
        return;
      }

      event.preventDefault();
      const cached = sessionStorage.getItem(SESSION_EMAIL_KEY);
      if (cached) {
        startDownload();
        return;
      }

      setError(null);
      setModalOpen(true);
    },
    [isSignedIn, alreadyBought],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(t("install.emailInvalid"));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await RestApi.recordDemoDownload(trimmed);
      sessionStorage.setItem(SESSION_EMAIL_KEY, trimmed);
      setModalOpen(false);
      startDownload();
    } catch {
      setError(t("install.emailSubmitError"));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadButtonProps = isSignedIn
    ? {
        component: "a" as const,
        href: DOWNLOAD_URL,
      }
    : {
        component: "button" as const,
        onClick: handleDownloadClick,
      };

  return (
    <NavBarContainer fullBleed showFooter>
      <Box
        style={{
          background:
            "linear-gradient(160deg, #fff8f2 0%, #ffffff 45%, #fff4eb 100%)",
          minHeight: "calc(100vh - 60px - 160px)",
        }}
        py={{ base: "xl", md: 48 }}
      >
        <Container size="lg">
          <Stack gap={48}>
            {/* Hero */}
            <Grid align="center">
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="lg">
                  <div>
                    <Title order={1} c="charcoal.8" mb="xs">
                      {t("install.title")}
                    </Title>
                    <Text c="dimmed" size="lg" maw={480}>
                      {t(
                        alreadyBought
                          ? "install.purchasedIntro"
                          : "install.intro",
                      )}
                    </Text>
                  </div>
                  <Group gap="md">
                    <Button
                      {...downloadButtonProps}
                      size="lg"
                      color="brand"
                      leftSection={<IconDownload size={20} />}
                    >
                      {t("nav.download")}
                    </Button>
                    {!alreadyBought && (
                      <Button
                        component={Link}
                        to="/buy"
                        size="lg"
                        variant="outline"
                        color="charcoal"
                      >
                        {t("install.buyApp")}
                      </Button>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {t("install.windowsNote")}
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Image
                  src={imgDinLabelLaptop}
                  alt="DinLabel"
                  maw={360}
                  mx="auto"
                  style={{
                    filter: "drop-shadow(0 20px 40px rgba(44, 45, 48, 0.16))",
                  }}
                />
              </Grid.Col>
            </Grid>

            {!alreadyBought && (
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap="md" p="xl" style={panelStyle} align="center">
                    <Image
                      src={imgEtiquetaConMarcaDeAgua}
                      alt=""
                      maw={200}
                      radius="sm"
                    />
                    <Title order={3} c="charcoal.8" ta="center">
                      {t("install.freeTag")}
                    </Title>
                    <Text ta="center" size="sm" c="dimmed" maw={320}>
                      {t("install.freeText")}
                    </Text>
                    <Button
                      {...downloadButtonProps}
                      color="brand"
                      fullWidth
                      maw={280}
                      leftSection={<IconDownload size={18} />}
                    >
                      {t("nav.download")}
                    </Button>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack
                    gap="md"
                    p="xl"
                    align="center"
                    style={{
                      ...panelStyle,
                      borderColor: "var(--mantine-color-brand-3)",
                      boxShadow: "0 8px 32px rgba(242, 122, 33, 0.12)",
                    }}
                  >
                    <Image
                      src={imgEtiquetaSinMarcaDeAgua}
                      alt=""
                      maw={200}
                      radius="sm"
                    />
                    <Title order={3} c="charcoal.8" ta="center">
                      {t("install.paidTitle")}
                    </Title>
                    <Text ta="center" size="sm" c="dimmed" maw={320}>
                      {t("install.paidText")}
                    </Text>
                    <Button
                      component={Link}
                      to="/buy"
                      color="brand"
                      fullWidth
                      maw={280}
                    >
                      {t("install.buyApp")}
                    </Button>
                  </Stack>
                </Grid.Col>
              </Grid>
            )}

            <Box p="xl" style={panelStyle}>
              <Title order={3} c="charcoal.8" mb="md">
                {t("install.signInTitle")}
              </Title>
              <List spacing="sm" type="ordered" c="charcoal.7">
                <List.Item>{t("install.signInStep1")}</List.Item>
                <List.Item>{t("install.signInStep2")}</List.Item>
                <List.Item>{t("install.signInStep3")}</List.Item>
                <List.Item>
                  {alreadyBought ? (
                    t("install.signInStep4")
                  ) : (
                    <>
                      {t("install.signInStep4UnpurchasedBefore")}{" "}
                      <Anchor component={Link} to="/buy" c="brand.7" fw={600}>
                        {t("install.signInStep4UnpurchasedLink")}
                      </Anchor>
                      {t("install.signInStep4UnpurchasedAfter")}
                    </>
                  )}
                </List.Item>
              </List>
            </Box>

            {/* Requirements */}
            <Box p="xl" style={panelStyle}>
              <Title order={3} c="charcoal.8" mb="md">
                {t("install.requirements")}
              </Title>
              <List
                spacing="sm"
                icon={
                  <IconCheck
                    size={16}
                    color="var(--mantine-color-brand-6)"
                  />
                }
              >
                <List.Item>
                  <Text c="charcoal.7">{t("install.reqWindows")}</Text>
                </List.Item>
                <List.Item>
                  <Text c="charcoal.7">{t("install.reqInternet")}</Text>
                </List.Item>
                <List.Item>
                  <Text c="charcoal.7">{t("install.reqDisk")}</Text>
                </List.Item>
              </List>
            </Box>

            {!alreadyBought && (
              <Text ta="center" size="sm" c="dimmed">
                {t("install.orDistributor")}{" "}
                <Anchor
                  component={Link}
                  to="/viewdistributors"
                  c="brand.7"
                  fw={600}
                >
                  {t("install.distributorsLink")}
                </Anchor>
              </Text>
            )}
          </Stack>
        </Container>
      </Box>

      <Modal
        opened={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title={t("install.emailModalTitle")}
        centered
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t("install.emailModalIntro")}
            </Text>
            <TextInput
              label={t("install.emailLabel")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              autoFocus
              disabled={submitting}
              error={error}
            />
            <Button type="submit" color="brand" loading={submitting}>
              {t("install.emailSubmit")}
            </Button>
          </Stack>
        </form>
      </Modal>
    </NavBarContainer>
  );
}
